import { fmtG, fmtTemp } from "./format";

export type UnitSystem = "metric" | "us";
export type IngredientKey =
  | "flour" | "water" | "sugar" | "oil" | "salt" | "yeast" | "butter" | "starter";

const G_PER_OZ = 28.3495;
const G_PER_LB = 453.592;

interface Density {
  gPerCup?: number;   // cup-capable ingredients
  gPerTbsp?: number;  // tbsp-based (oil, butter)
  gPerTsp?: number;   // spoon-based (salt, yeast)
  weightOnly?: boolean;
  noTsp?: boolean;    // cup-capable ingredient that never drops below tbsp (water)
  noPromote?: boolean; // spoon ingredient that never promotes to tbsp (yeast)
}

// Per-ingredient unit ladders (auto-picked by size) match the design table:
//   flour/sugar cups->tbsp->tsp, water cups->tbsp, oil tbsp->tsp,
//   salt tsp->tbsp, yeast tsp, butter tbsp, starter weight-only.
const DENSITY: Record<IngredientKey, Density> = {
  flour:   { gPerCup: 120 },
  water:   { gPerCup: 237, noTsp: true },
  sugar:   { gPerCup: 200 },
  oil:     { gPerTbsp: 13.5 },
  salt:    { gPerTsp: 6 },
  yeast:   { gPerTsp: 3.1, noPromote: true },
  butter:  { gPerTbsp: 14.2 },
  starter: { weightOnly: true },
};

export function ingredientKey(name: string): IngredientKey | null {
  const n = name.toLowerCase();
  if (n.startsWith("flour")) return "flour";
  if (n.startsWith("water")) return "water";
  if (n.startsWith("sugar")) return "sugar";
  if (n.startsWith("oil")) return "oil";
  if (n.startsWith("salt")) return "salt";
  if (n.startsWith("yeast")) return "yeast";
  if (n.startsWith("butter")) return "butter";
  if (n.startsWith("starter")) return "starter";
  return null;
}

export function fmtWeightHint(grams: number): string {
  const oz = grams / G_PER_OZ;
  if (oz >= 16) return (grams / G_PER_LB).toFixed(2) + " lb";
  return oz.toFixed(1) + " oz";
}

export function fmtTempSys(celsius: number, sys: UnitSystem): string {
  if (sys === "us") return Math.round(celsius * 9 / 5 + 32) + " °F";
  return fmtTemp(celsius) + " °C";
}

type Vol = { qty: number; unit: "cup" | "tbsp" | "tsp" };

function toVolume(grams: number, d: Density): Vol {
  if (d.gPerCup != null) {
    const cups = grams / d.gPerCup;
    if (cups >= 0.25) return { qty: cups, unit: "cup" };
    const tbsp = cups * 16;
    if (d.noTsp || tbsp >= 1) return { qty: tbsp, unit: "tbsp" };
    return { qty: tbsp * 3, unit: "tsp" };
  }
  if (d.gPerTbsp != null) {
    const tbsp = grams / d.gPerTbsp;
    if (tbsp >= 1) return { qty: tbsp, unit: "tbsp" };
    return { qty: tbsp * 3, unit: "tsp" };
  }
  const tsp = grams / d.gPerTsp!;
  if (!d.noPromote && tsp >= 3) return { qty: tsp / 3, unit: "tbsp" };
  return { qty: tsp, unit: "tsp" };
}

const FRACTIONS: [number, string][] = [
  [0, ""], [1 / 8, "⅛"], [1 / 4, "¼"], [1 / 3, "⅓"],
  [1 / 2, "½"], [2 / 3, "⅔"], [3 / 4, "¾"], [1, ""],
];

// Whole number plus nearest kitchen fraction, e.g. "1½", "¾", "2".
function snapFraction(qty: number): string {
  let whole = Math.floor(qty);
  const frac = qty - whole;
  let best = FRACTIONS[0]!;
  let bestD = Infinity;
  for (const f of FRACTIONS) {
    const dd = Math.abs(frac - f[0]);
    if (dd < bestD) { bestD = dd; best = f; }
  }
  let sym = best[1];
  if (best[0] === 1) { whole += 1; sym = ""; }
  if (whole === 0 && sym === "") return "0";
  if (whole === 0) return sym;
  if (sym === "") return String(whole);
  return `${whole}${sym}`;
}

function unitLabel(unit: Vol["unit"], amountLabel: string): string {
  if (unit === "cup") {
    // Singular for: "1" or pure fractions like "½"
    // Plural for: everything else (2, 3, 1½, 1¾, etc.)
    if (amountLabel === "1") return "cup";
    const firstChar = amountLabel[0]!;
    if (firstChar === "½" || firstChar === "¼" || firstChar === "⅓" || firstChar === "¾" || firstChar === "⅔" || firstChar === "⅛") {
      return "cup"; // pure fraction
    }
    return "cups"; // 2, 3, 1½, 1¾, etc.
  }
  return unit; // tbsp / tsp invariant
}

export function fmtWeight(grams: number, key: IngredientKey | null, sys: UnitSystem): string {
  if (sys === "metric") return fmtG(grams);
  const d = key ? DENSITY[key] : null;
  if (!d || d.weightOnly) return fmtWeightHint(grams);
  const v = toVolume(grams, d);
  const amount = snapFraction(v.qty);
  return `${amount} ${unitLabel(v.unit, amount)} (${fmtWeightHint(grams)})`;
}
